/**
 * Dataspace Tree Store - Global filesystem tree for entire dataspace
 * 
 * Data Model: Edge documents + fractional indexing (same as spaceTreeStore)
 * - Each edge: (dataspace_id, parent_id, child_id, pos, expanded)
 * - Edges stored in separate collection (not on entity.skeleton)
 * - Supports aliases (same entity can have multiple edges)
 * 
 * Key Differences from SpaceTreeStore:
 * - ✅ No spaceId filtering - shows ALL entities in dataspace
 * - ✅ Persists to dataspace_edges API (not entity.skeleton)
 * - ✅ Supports aliases (entity in multiple locations)
 * - ✅ User-agnostic (shared tree for entire dataspace/org)
 * 
 * Architecture (same as spaceTreeStore):
 * - ✅ Optimistic updates (instant UI feedback)
 * - ✅ Operation log for persistence (debounced flush)
 * - ✅ Subscribes to entityStore for reactive node creation/deletion
 */

import { create } from 'zustand';
import { BaseEntityType } from '../types/entities';
import useEntityStore from './entityStore';
import { createFileLogger } from '../utils/logger';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';

const log = createFileLogger('DATASPACE_TREE_STORE', { enabled: true });
const VERBOSE_LOGGING = false;

const TREE_STORE_VERSION = 'v2.0-alias-edges'; // ✅ Changed: Now supports aliases with edgeKey format
log.log('🌲 [DataspaceTreeStore] Loaded version:', TREE_STORE_VERSION);

// ✅ MIGRATION: Force clear tree on version change
const STORED_VERSION_KEY = 'dataspaceTreeStore_version';
const storedVersion = localStorage.getItem(STORED_VERSION_KEY);
if (storedVersion !== TREE_STORE_VERSION) {
  log.log('🔄 [DataspaceTreeStore] Version changed from', storedVersion, 'to', TREE_STORE_VERSION);
  log.log('🔄 [DataspaceTreeStore] Clearing old tree data and forcing refresh');
  localStorage.setItem(STORED_VERSION_KEY, TREE_STORE_VERSION);
  // Set flag to force refresh on next initialize()
  (window as any).__forceDataspaceTreeRefresh = true;
  
  // ✅ CRITICAL: Also immediately reset the store state to clear old format data
  // This ensures any already-created store instance gets cleared
  setTimeout(() => {
    const store = useDataspaceTreeStore.getState();
    if (Object.keys(store.nodesById).length > 0) {
      log.log('🔄 [DataspaceTreeStore] Clearing', Object.keys(store.nodesById).length, 'old format nodes');
      // Capture dataspaceId before clearing — if initialize() already ran (race condition),
      // we must re-run it after clearing since the component's useEffect won't re-fire.
      const prevDataspaceId = store.dataspaceId;
      useDataspaceTreeStore.setState({
        nodesById: {},
        childrenByParentId: new Map(),
        initialized: false,
        rebuildCount: 0,
        dataspaceId: null
      });
      if (prevDataspaceId) {
        log.log('🔄 [DataspaceTreeStore] Re-initializing after version migration clear:', prevDataspaceId);
        useDataspaceTreeStore.getState().initialize(prevDataspaceId);
      }
    }
  }, 0);
}

// ========================================================================
// Fractional Indexing (copied from spaceTreeStore)
// ========================================================================

function generatePositionBetween(before: string | null, after: string | null): string {
  // ✅ CRITICAL FIX: Detect and handle position collision
  // This can happen due to race conditions or database inconsistencies
  if (before && after && before === after) {
    log.warn('[FractionalIndex] ⚠️ Position collision detected!', { 
      before, 
      after, 
      note: 'Appending digit to create unique position' 
    });
    // Append '0' to create unique position after the collision point
    return before + '0';
  }
  
  if (!before && !after) return 'M';
  if (!after) return before + 'M';
  if (!before) {
    const firstChar = after[0];
    const firstVal = charToBase36(firstChar);
    if (firstVal > 0) {
      const mid = Math.floor(firstVal / 2);
      return base36ToChar(mid);
    }
    return '!' + after;
  }
  
  let pos = 0;
  while (pos < before.length && pos < after.length && before[pos] === after[pos]) {
    pos++;
  }
  
  const prefix = before.slice(0, pos);
  
  if (pos === before.length) {
    const afterSuffix = after.slice(before.length);
    const firstAfterChar = afterSuffix[0];
    const firstAfterVal = charToBase36(firstAfterChar);
    
    if (firstAfterVal >= 2) {
      const midVal = Math.floor(firstAfterVal / 2);
      return before + base36ToChar(midVal);
    }
    return before + '0' + 'M';
  }
  
  if (pos === after.length) {
    return after + 'V';
  }
  
  const beforeChar = before[pos];
  const afterChar = after[pos];
  const beforeVal = charToBase36(beforeChar);
  const afterVal = charToBase36(afterChar);
  
  if (afterVal - beforeVal >= 2) {
    const midVal = Math.floor((beforeVal + afterVal) / 2);
    return prefix + base36ToChar(midVal);
  }
  
  return before + 'M';
}

function charToBase36(char: string): number {
  const code = char.charCodeAt(0);
  if (code === 33) return -1;
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 65 && code <= 90) return code - 65 + 10;
  if (code >= 97 && code <= 122) return code - 97 + 36;
  return 0;
}

function base36ToChar(val: number): string {
  if (val < 10) return String.fromCharCode(val + 48);
  if (val < 36) return String.fromCharCode(val - 10 + 65);
  if (val < 62) return String.fromCharCode(val - 36 + 97);
  return '0';
}

function generateCleanPosition(index: number): string {
  if (index < 10) {
    return String.fromCharCode(48 + index);
  } else if (index < 36) {
    return String.fromCharCode(65 + (index - 10));
  } else if (index < 62) {
    return String.fromCharCode(97 + (index - 36));
  } else {
    const multiIndex = index - 62;
    const firstIndex = Math.floor(multiIndex / 26);
    const secondIndex = multiIndex % 26;
    const firstChar = String.fromCharCode(48 + (firstIndex % 10));
    const secondChar = String.fromCharCode(65 + secondIndex);
    return firstChar + secondChar;
  }
}

// ========================================================================
// Tree Node & Edge Structures
// ========================================================================

interface TreeNode {
  id: string; // Entity ID
  parentId: string | null;
  pos: string;
  expanded: boolean;
  edgeId?: string; // ✅ Track which edge document this came from (unique identifier for this specific edge)
  kind?: 'canonical' | 'alias'; // ✅ Edge type
}

// ✅ Helper to create unique edge key for alias support
// EXPORTED for use in other components
export function getEdgeKey(entityId: string, parentId: string | null): string {
  return `${parentId || 'ROOT'}:${entityId}`;
}

interface DataspaceEdge {
  id: string;
  dataspaceId: string;
  parentId: string | null;
  childId: string;
  pos: string;
  expanded: boolean;
  kind: 'canonical' | 'alias';
  createdAt: string;
  updatedAt: string;
}

type TreeOperation = 
  | { type: 'move'; nodeId: string; parentId: string | null; pos: string; edgeId?: string; timestamp: number }
  | { type: 'expand'; nodeId: string; expanded: boolean; edgeId?: string; timestamp: number }
  | { type: 'create'; nodeId: string; parentId: string | null; pos: string; kind: 'canonical' | 'alias'; timestamp: number }
  | { type: 'delete'; edgeId?: string; nodeId?: string; parentId?: string | null; timestamp: number };

// ========================================================================
// Store State
// ========================================================================

interface DataspaceTreeState {
  dataspaceId: string | null;
  nodesById: Record<string, TreeNode>; // ✅ CHANGED: Now keyed by edgeKey (parent:child) to support aliases
  childrenByParentId: Map<string | null, string[]>;
  pendingOps: TreeOperation[];
  lastFlushTime: number;
  flushTimeoutId: NodeJS.Timeout | null;  // ✅ Track timeout to prevent race conditions
  isFlushInProgress: boolean;             // Mutex: prevents concurrent _flushToBackend calls
  refreshIntervalId: NodeJS.Timeout | null;  // ✅ Track refresh interval for edge polling
  autoRefreshTimeoutId: NodeJS.Timeout | null;  // ✅ Track auto-refresh debounce timeout
  initialized: boolean;
  rebuildCount: number;
  entityStoreUnsubscribe: (() => void) | null;
  entityEventUnsubscribe: (() => void) | null;  // ✅ NEW: Track entity event listener
}

interface DataspaceTreeStore extends DataspaceTreeState {
  initialize: (dataspaceId: string) => Promise<void>;
  initFromEdges: (edges: DataspaceEdge[]) => void;
  refreshEdges: (opts?: { fetchMissingEntities?: boolean }) => Promise<void>;
  migrateSpaceEntities: (spaceId: string) => Promise<void>;  // ✅ NEW: Soft migration for space
  
  moveNode: (nodeId: string, newParentId: string | null, afterNodeId: string | null, fromParentId?: string | null) => void;
  toggleExpanded: (nodeId: string) => void;
  expandNode: (nodeId: string) => void;
  collapseNode: (nodeId: string) => void;
  expandAllNodes: () => { count: number };
  collapseAllNodes: () => { count: number };
  ensureNodeExists: (nodeId: string, parentId: string | null, kind?: 'canonical' | 'alias') => void;
  addAliasEdge: (entityId: string, parentId: string | null) => Promise<void>;
  
  getRootNodeIds: () => string[];
  getChildNodeIds: (parentId: string | null) => string[];
  getNode: (nodeId: string, parentId?: string | null) => TreeNode | undefined;
  isExpanded: (nodeId: string, parentId?: string | null) => boolean;
  hasChildren: (nodeId: string) => boolean;
  
  _rebuildChildrenMap: () => void;
  _applyAddNode: (edgeKey: string, node: TreeNode, operation?: TreeOperation) => void;
  _applyRemoveNode: (edgeKey: string, options?: { persist?: boolean }) => void;
  _scheduleFlush: (immediate?: boolean) => void;
  _flushToBackend: () => Promise<void>;
  
  cleanup: () => void;
  debugPrintTree: () => void;
}

// ========================================================================
// Helper: Filter displayable entities
// ========================================================================

function isDisplayableEntity(entity: BaseEntityType): boolean {
  // ✅ Spaces ARE displayable - they're organizeable in the tree!
  const hiddenTypes = ['Message', 'DocVersion'];
  if (hiddenTypes.includes(entity.entityType)) return false;
  if ((entity as any).hidden === true) return false;
  if ((entity as any).trashed === true) return false;
  
  // ✅ Integration data entities are system-managed and should NOT appear in the
  // space sidebar tree. They're queryable via river.entity.query() in apps but
  // are not user-organizable documents. They either have no spaceId (bank data,
  // integration state) or belong to a specific integration hierarchy.
  const integrationDataTypes = [
    'BankTransaction',
    'PlaidItem',
    'PlaidTransaction',
    'PlaidAccount',
    'Integration',
    'GoogleDriveFile',
    'LocalFile',
  ];
  if (integrationDataTypes.includes(entity.entityType)) return false;
  
  // ✅ Entities with no spaceId that are NOT explicitly user-organizable content
  // should not appear in the sidebar tree. User content (Docs, Notes, Sheets etc.)
  // always has a spaceId from creation. Integration-sourced entities typically don't.
  const userContentTypes = [
    'Doc', 'Note', 'Sheet', 'Canvas', 'Presentation',
    'Group', 'Space', 'Thread', 'Tool', 'Corpus', 'Dataset',
    'Rule',
    'app_project', 'website_project', 'code_project',
    'APIConnection', 'MCPServer',
    'File', 'File.Image', 'File.Video', 'File.Document', 'File.Document.PDF',
    'StoryCharacter', 'Playbook',
  ];
  
  // If entity type is not a recognized user content type AND has no spaceId, hide it
  const entityType = entity.entityType || '';
  const isKnownUserContent = userContentTypes.some(t => 
    entityType === t || entityType.startsWith(t + '.')
  );
  const hasSpaceId = !!(entity as any).spaceId;
  
  if (!isKnownUserContent && !hasSpaceId) return false;
  
  return true;
}

// ========================================================================
// Module-level debounce timer for edge re-sync
// Used to coalesce multiple new-entity arrivals into a single refreshEdges() call
// ========================================================================
let _edgeRefreshTimer: ReturnType<typeof setTimeout> | null = null;

// ========================================================================
// Store Implementation
// ========================================================================

export const useDataspaceTreeStore = create<DataspaceTreeStore>((set, get) => ({
  // Initial state
  dataspaceId: null,
  nodesById: {},
  childrenByParentId: new Map(),
  pendingOps: [],
  lastFlushTime: 0,
  flushTimeoutId: null,
  isFlushInProgress: false,
  refreshIntervalId: null,
  autoRefreshTimeoutId: null,
  initialized: false,
  rebuildCount: 0,
  entityStoreUnsubscribe: null,
  entityEventUnsubscribe: null,  // ✅ NEW: Initialize entity event listener tracker
  
  // ========================================================================
  // Initialization
  // ========================================================================
  
  initialize: async (dataspaceId: string) => {
    const { initialized: currentlyInit, dataspaceId: currentDataspaceId, entityStoreUnsubscribe, entityEventUnsubscribe } = get();
    
    log.log('[DataspaceTreeStore] 🚀 Initializing for dataspace:', dataspaceId);
    
    // Cleanup previous subscriptions if switching dataspaces
    if (currentlyInit && currentDataspaceId !== dataspaceId) {
      if (entityStoreUnsubscribe) {
        log.log('[DataspaceTreeStore] Switching dataspace, cleaning up old entity store subscription');
        entityStoreUnsubscribe();
        set({ entityStoreUnsubscribe: null });
      }
      if (entityEventUnsubscribe) {
        log.log('[DataspaceTreeStore] Switching dataspace, cleaning up old entity event subscription');
        entityEventUnsubscribe();
        set({ entityEventUnsubscribe: null });
      }
    }
    
    // ✅ CRITICAL: Clear pending flush timeout when switching dataspaces
    const { flushTimeoutId, refreshIntervalId, autoRefreshTimeoutId } = get();
    if (flushTimeoutId) {
      log.log('[DataspaceTreeStore] Clearing flush timeout on dataspace switch');
      clearTimeout(flushTimeoutId);
      set({ flushTimeoutId: null });
    }
    
    // ✅ CRITICAL: Clear refresh interval when switching dataspaces
    if (refreshIntervalId) {
      log.log('[DataspaceTreeStore] Clearing refresh interval on dataspace switch');
      clearInterval(refreshIntervalId);
      set({ refreshIntervalId: null });
    }
    
    // ✅ CRITICAL: Clear auto-refresh timeout when switching dataspaces
    if (autoRefreshTimeoutId) {
      log.log('[DataspaceTreeStore] Clearing auto-refresh timeout on dataspace switch');
      clearTimeout(autoRefreshTimeoutId);
      set({ autoRefreshTimeoutId: null });
    }
    
    // ✅ FORCE REFRESH: Check for version migration flag or old key format
    const forceRefresh = (window as any).__forceDataspaceTreeRefresh;
    
    if (currentlyInit && currentDataspaceId === dataspaceId) {
      log.log('[DataspaceTreeStore] Already initialized for this dataspace');
      
      if (forceRefresh) {
        log.log('[DataspaceTreeStore] 🔄 Version migration - forcing refresh from backend');
        (window as any).__forceDataspaceTreeRefresh = false;
        // Clear old data and re-initialize
        set({ 
          nodesById: {},
          childrenByParentId: new Map(),
          initialized: false,
          rebuildCount: 0
        });
      } else {
        // Check if nodesById uses old key format
        const { nodesById } = get();
        const firstKey = Object.keys(nodesById)[0];
        const usesOldFormat = firstKey && !firstKey.includes(':');
        
        if (usesOldFormat) {
          log.log('[DataspaceTreeStore] ⚠️ Old key format detected, forcing refresh from backend');
          // Clear old data and re-initialize
          set({ 
            nodesById: {},
            childrenByParentId: new Map(),
            initialized: false,
            rebuildCount: 0
          });
        } else {
          return;
        }
      }
    }
    
    set({ dataspaceId, initialized: false });
    console.log('[TREE_INIT] 🌲 initialize() called with dataspaceId:', dataspaceId);
    
    try {
      // ── Fetch edges from REST (authoritative source) ─────────────────────
      // Note: We previously tried bootstrapping from entity-system Y.Doc edges,
      // but that data is incomplete (only contains edges broadcast AFTER the
      // entity-system broadcast code was added). Historical edges are missing,
      // causing phantom "entities without edges" that create duplicate edges.
      // REST is always authoritative for the initial tree build.
      const url = `${API_BASE_URL}/dataspaces/${dataspaceId}/edges`;
      log.log('[DataspaceTreeStore] Fetching edges from API:', url);
      
      const response = await fetchService(url, {
        credentials: 'include',
      });
      
      // ✅ VALIDATE: Check if we got HTML (404) instead of JSON
      if (typeof response === 'string' && response.includes('<!doctype html>')) {
        console.error('[DataspaceTreeStore] ❌ Got HTML instead of JSON - route not found!');
        throw new Error('API route not found - backend may need restart after adding new routes');
      }
      
      if (!Array.isArray(response)) {
        console.error('[DataspaceTreeStore] ❌ Expected array, got:', typeof response);
        throw new Error(`Expected array of edges, got ${typeof response}`);
      }
      
      const edges: DataspaceEdge[] = response;
      console.log(`[TREE_INIT] 📦 Loaded ${edges.length} edges from backend for dataspace ${dataspaceId}`);
      if (edges.length > 0) {
        const parentCounts: Record<string, number> = {};
        edges.forEach(e => { const p = e.parentId ?? 'null'; parentCounts[p] = (parentCounts[p] || 0) + 1; });
        console.log('[TREE_INIT] Edge parent distribution:', parentCounts);
      }
      log.log('[DataspaceTreeStore] Loaded', edges.length, 'edges from backend');
      
      // Build tree from edges
      get().initFromEdges(edges);
      
      const treeNodeCount = Object.keys(get().nodesById).length;
      console.log(`[TREE_INIT] 🌳 Tree built: ${treeNodeCount} nodes in nodesById`);
      
      // Wait for entityStore to be ready before comparing tree vs entities.
      // If entityStore is still loading, entities will be empty and we'd
      // misidentify all entities as missing edges, triggering unnecessary
      // bootstrap/repair and showing trashed entities in the tree.
      const entityStoreState = useEntityStore.getState();
      if (entityStoreState.lifecycle !== 'ready' && entityStoreState.entities.length === 0) {
        log.log('[DataspaceTreeStore] entityStore not ready yet, waiting...');
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(resolve, 3000)
          const unsub = useEntityStore.subscribe((s: any) => {
            if (s.lifecycle === 'ready' || s.entities.length > 0) {
              clearTimeout(timeout)
              unsub()
              resolve()
            }
          })
        })
      }

      const allEntities = useEntityStore.getState().entities;
      const displayableEntities = allEntities.filter(isDisplayableEntity);
      
      // Find entities with NO edges at all — parentId=null is valid (Drive root placement)
      const entitiesWithBadEdges = displayableEntities.filter(entity => {
        const hasAnyEdge = edges.some(edge => edge.childId === entity._id);
        return !hasAnyEdge;
      });
      
      // Calculate completeness metrics
      const totalDisplayable = displayableEntities.length;
      const missingCount = entitiesWithBadEdges.length;
      const missingPercentage = totalDisplayable > 0 ? (missingCount / totalDisplayable * 100) : 0;
      
      // ✅ ROBUST REPAIR: Trigger bootstrap if ANY entities have bad edges
      // Detects both missing edges AND orphaned edges (parent_id: null)
      const needsBootstrap = (
        treeNodeCount === 0 ||    // Empty tree
        missingCount > 0          // ANY missing or malformed edges
      );
      
      if (needsBootstrap) {
        const reason = treeNodeCount === 0 
          ? 'tree is empty (zero edges)' 
          : `corruption detected (${missingCount}/${totalDisplayable} entities with bad edges = ${missingPercentage.toFixed(1)}%)`;
        
        log.warn(`[DataspaceTreeStore] ⚠️ Lazy repair triggered: ${reason}`);
        
        // Log sample of bad edges for debugging
        if (missingCount > 0) {
          const sample = entitiesWithBadEdges.slice(0, 5).map(e => `${e.entityType}: ${e.name || 'untitled'}`);
          log.warn(`[DataspaceTreeStore] Bad edges for entities: ${sample.join(', ')}${missingCount > 5 ? ` ... and ${missingCount - 5} more` : ''}`);
        }
        
        try {
          log.log('[DataspaceTreeStore] Calling bootstrap endpoint...');
          const bootstrapResult = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/bootstrap`, {
            method: 'POST',
            credentials: 'include',
          });
          
          const created = bootstrapResult.created || 0;
          const repaired = bootstrapResult.repaired || 0;
          const totalFixed = bootstrapResult.total_fixed || created + repaired;
          
          log.log(`[DataspaceTreeStore] ✅ Repair complete: ${created} created, ${repaired} repaired (${totalFixed} total)`);
          
          // Reload edges and rebuild tree
          const reloadedEdges: DataspaceEdge[] = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges`, {
            credentials: 'include',
          });
          
          log.log(`[DataspaceTreeStore] Reloaded ${reloadedEdges.length} edges after repair, rebuilding tree...`);
          
          // Rebuild tree with new edges
          get().initFromEdges(reloadedEdges);
          
          // ✅ VERIFICATION: Check if ALL entities now have edges
          // Re-fetch entities to avoid stale reference (entities may have changed during repair)
          const allEntitiesAfterRepair = useEntityStore.getState().entities;
          const displayableEntitiesAfterRepair = allEntitiesAfterRepair.filter(isDisplayableEntity);
          
          const stillMissing = displayableEntitiesAfterRepair.filter(entity => 
            !reloadedEdges.some(edge => edge.childId === entity._id)
          );
          
          if (stillMissing.length === 0) {
            log.log('[DataspaceTreeStore] ✅ Repair successful - ALL entities now have edges');
          } else {
            log.warn(`[DataspaceTreeStore] ⚠️ ${stillMissing.length}/${displayableEntitiesAfterRepair.length} entities STILL missing edges after repair`);
            
            // Log sample of entities that are still broken
            const sample = stillMissing.slice(0, 5).map(e => `${e.entityType}: ${e.name || 'untitled'} (${e._id})`);
            log.warn(`[DataspaceTreeStore] Still broken: ${sample.join(', ')}${stillMissing.length > 5 ? ` ... and ${stillMissing.length - 5} more` : ''}`);
            
            // TODO: User notification (Phase 4)
            // showToast({ type: 'warning', message: 'Some files couldn't be repaired...' });
          }
        } catch (bootstrapError) {
          log.error('[DataspaceTreeStore] ❌ Repair failed:', bootstrapError);
          // ✅ ROBUST FALLBACK: If repair fails, skeleton-based fallback will show entities at root
          // TODO: User notification (Phase 4)
        }
      } else {
        // Tree is healthy - log for monitoring
        if (totalDisplayable > 0) {
          log.log(`[DataspaceTreeStore] ✅ Tree healthy: ${totalDisplayable - missingCount}/${totalDisplayable} entities have edges (${(100 - missingPercentage).toFixed(1)}% complete)`);
        } else {
          log.log(`[DataspaceTreeStore] ✅ Tree healthy: no displayable entities in workspace`);
        }
      }
      
      // ✅ SUBSCRIBE: Listen for entity changes
      const unsubscribe = useEntityStore.subscribe((state: any, prevState: any) => {
        const { dataspaceId: currentDataspaceId } = get();
        if (!currentDataspaceId) return;
        
        // Detect new entities
        const newEntities = state.entities.filter((e: BaseEntityType) => 
          !prevState.entities.find((prev: BaseEntityType) => prev._id === e._id) &&
          isDisplayableEntity(e)
        );
        
        // Detect deleted entities
        const deletedEntityIds = prevState.entities
          .filter((e: BaseEntityType) => !state.entities.find((curr: BaseEntityType) => curr._id === e._id))
          .map((e: BaseEntityType) => e._id);
        
        // ✅ CRITICAL FIX: DO NOT auto-create edges for new entities!
        // The UI components (SpaceSidebarV2, DataspaceSidebar) create edges explicitly
        // with the correct parent context. Auto-creating edges here would:
        // 1. Create duplicate edges (one from UI, one from here)
        // 2. Use wrong parent (null/root instead of space/folder)
        // 3. Cause documents to disappear from spaces after reload
        //
        // REMOVED: Auto-creation of edges at root level
        // if (newEntities.length > 0) {
        //   log.log('[DataspaceTreeStore] 📥 Detected', newEntities.length, 'new entities');
        //   newEntities.forEach((entity: BaseEntityType) => {
        //     ensureNodeExists(entity._id, null);
        //     moveNode(entity._id, null, null);
        //   });
        // }
        
        if (deletedEntityIds.length > 0) {
          // Guard: if the new entity list is empty but the previous list was non-empty,
          // this is almost certainly a bulk reload in two steps (clear then repopulate),
          // not genuine deletions. Treating it as deletions would flush DELETE requests
          // for every edge in the tree, permanently wiping the entire dataspace tree.
          // We skip deletion detection in this case; the subsequent repopulate event
          // (state.entities.length > 0) will correctly add back any new entities.
          if (state.entities.length === 0 && prevState.entities.length > 0) {
            log.warn('[DataspaceTreeStore] entityStore cleared — skipping deletion detection to prevent mass edge wipe');
            return;
          }

          log.log('[DataspaceTreeStore] 🗑️ Detected', deletedEntityIds.length, 'deleted entities');
          const currentNodesById = get().nodesById;
          deletedEntityIds.forEach((entityId: string) => {
            // nodesById is keyed by edgeKey ("parentId:childId"), not bare entityId
            Object.keys(currentNodesById).forEach(edgeKey => {
              if (currentNodesById[edgeKey].id === entityId) {
                get()._applyRemoveNode(edgeKey, { persist: true });
              }
            });
          });
        }
        
        // ✅ RACE CONDITION FIX: When new entities arrive (from background entity refresh)
        // that have no edge in the current tree, the tree was likely initialized while the
        // entity cache was stale (pre-upload). Re-fetch edges so the orphan filter in
        // initFromEdges doesn't permanently discard them.
        if (newEntities.length > 0) {
          const { nodesById } = get();
          const hasNewEntitiesWithoutEdges = newEntities.some((entity: BaseEntityType) =>
            !Object.values(nodesById).some((node: any) => node.id === entity._id)
          );
          if (hasNewEntitiesWithoutEdges) {
            log.log('[DataspaceTreeStore] 🔄 New entities without tree edges detected, scheduling edge re-sync:', newEntities.length);
            if (_edgeRefreshTimer) clearTimeout(_edgeRefreshTimer);
            _edgeRefreshTimer = setTimeout(() => {
              _edgeRefreshTimer = null;
              get().refreshEdges();
            }, 300);
          }
        }
      });
      
      console.log(`[TREE_INIT] ✅ initialized=true — tree is ready. Nodes: ${Object.keys(get().nodesById).length}`);
      set({ 
        initialized: true,
        entityStoreUnsubscribe: unsubscribe
      });
      
      // ✅ NEW: Auto-refresh edges on entity creation events
      // This eliminates need for manual refreshEdges() calls in AI commands
      (async () => {
        try {
          const { entityEventService } = await import('../services/entityEventService');
          
          const eventUnsubscribe = entityEventService.onEntityEvent('created', async (event) => {
            const { dataspaceId: currentDataspaceId, initialized: isInit, autoRefreshTimeoutId } = get();
            
            // Only refresh if initialized and entity is displayable
            if (!isInit || !currentDataspaceId) return;
            
            if (!isDisplayableEntity(event.entity)) return;
            
            log.log('[DataspaceTreeStore] 🔄 Auto-refreshing edges after entity creation:', {
              entityId: event.entity._id,
              entityType: event.entity.entityType,
              entityName: event.entity.name
            });
            
            // ✅ DEBOUNCE: Cancel previous timeout to prevent duplicate refreshes
            // This ensures batch operations (creating 10 docs) only trigger ONE refresh
            if (autoRefreshTimeoutId) {
              clearTimeout(autoRefreshTimeoutId);
            }
            
            // Schedule refresh after 100ms (wait for batch operations to complete)
            const timeoutId = setTimeout(async () => {
              try {
                await get().refreshEdges();
                log.log('[DataspaceTreeStore] ✅ Auto-refresh complete');
                set({ autoRefreshTimeoutId: null });
              } catch (error) {
                log.error('[DataspaceTreeStore] ❌ Auto-refresh failed:', error);
                set({ autoRefreshTimeoutId: null });
              }
            }, 100);
            
            set({ autoRefreshTimeoutId: timeoutId });
          });
          
          set({ entityEventUnsubscribe: eventUnsubscribe });
          log.log('[DataspaceTreeStore] ✅ Subscribed to entity creation events for auto-refresh');
        } catch (error) {
          log.error('[DataspaceTreeStore] Failed to subscribe to entity events:', error);
        }
      })();
      
      // Periodic polling removed — the org Y.Doc (org:{dataspaceId}) provides
      // real-time edge updates via WebSocket. No polling needed.
      
      log.log('[DataspaceTreeStore] ✅ Initialization complete');
      
    } catch (error) {
      console.error('[TREE_INIT] ❌ Initialization FAILED:', error);
      log.error('[DataspaceTreeStore] ❌ Initialization failed:', error);
      set({ initialized: false });
      // Retry once after 3 seconds — guards against transient network errors on page load.
      // Only retry if the dataspace hasn't changed (user hasn't navigated away).
      setTimeout(() => {
        const { dataspaceId: currentId, initialized: isInit } = get();
        if (!isInit && currentId === dataspaceId) {
          log.log('[DataspaceTreeStore] 🔄 Retrying initialization for:', dataspaceId);
          get().initialize(dataspaceId);
        }
      }, 3000);
    }
  },
  
  initFromEdges: (edges: DataspaceEdge[]) => {
    log.log('[DataspaceTreeStore] 🔨 Building tree from', edges.length, 'edges');
    
    // ✅ SAFETY NET: Filter out orphaned edges (pointing to deleted/trashed entities)
    // IMPORTANT: Only filter entities explicitly trashed, not entities that are
    // simply not loaded yet in the cache (background refresh may still be in progress)
    const allEntitiesFromStore = useEntityStore.getState().entities;
    const trashedEntityIds = new Set(
      allEntitiesFromStore.filter((e: any) => e.trashed === true).map((e: any) => e._id)
    );
    log.log('[DataspaceTreeStore] 📦 Total entities in store:', allEntitiesFromStore.length);
    
    const validEdges = edges.filter((edge: DataspaceEdge) => {
      // Only reject if entity is explicitly trashed
      if (trashedEntityIds.has(edge.childId)) {
        // log.warn('[DataspaceTreeStore] 🗑️ Ignoring orphaned edge for deleted entity:', edge.childId);
        return false;
      }
      return true;
    });
    
    if (validEdges.length < edges.length) {
      log.warn('[DataspaceTreeStore] ⚠️ Filtered out', edges.length - validEdges.length, 'orphaned edges');
    }
    
    log.log('[DataspaceTreeStore] ✅ Valid edges after orphan filter:', validEdges.length);
    
    // ✅ DEDUPLICATION: Handle duplicate edges for same entity
    // Prioritize: non-null parentId > null parentId, and numeric positions > letter positions
    const edgesByChild = new Map<string, DataspaceEdge[]>();
    
    validEdges.forEach((edge: DataspaceEdge) => {
      const existing = edgesByChild.get(edge.childId) || [];
      existing.push(edge);
      edgesByChild.set(edge.childId, existing);
    });
    
    const deduplicatedEdges: DataspaceEdge[] = [];
    let duplicateCount = 0;
    
    edgesByChild.forEach((entityEdges, childId) => {
      if (entityEdges.length === 1) {
        deduplicatedEdges.push(entityEdges[0]);
      } else {
        // Multiple edges for same entity
        // Future-proof: If aliases are implemented (kind='alias'), keep all edges
        // For now: Deduplicate canonical edges only (duplicates are migration artifacts)
        const canonicalEdges = entityEdges.filter(e => e.kind !== 'alias');
        const aliasEdges = entityEdges.filter(e => e.kind === 'alias');
        
        if (canonicalEdges.length > 1) {
          // True duplicates - pick the best canonical edge
          duplicateCount += canonicalEdges.length - 1;
          
          // Sort by priority: non-null parentId first, then by position style (numeric > letter)
          const sorted = canonicalEdges.sort((a, b) => {
            // 1. Prefer non-null parentId (entities in folders > entities in root)
            if (a.parentId !== null && b.parentId === null) return -1;
            if (a.parentId === null && b.parentId !== null) return 1;
            
            // 2. Prefer numeric/fractional positions over old "M" style
            const aIsOld = typeof a.pos === 'string' && /^M+$/.test(a.pos);
            const bIsOld = typeof b.pos === 'string' && /^M+$/.test(b.pos);
            if (!aIsOld && bIsOld) return -1;
            if (aIsOld && !bIsOld) return 1;
            
            // 3. Prefer NEWER edge (higher MongoDB ObjectId = created more recently)
            // This ensures correct edges from latest uploads win over stale ones
            return (b.id || '').localeCompare(a.id || '');
          });
          
          deduplicatedEdges.push(sorted[0]);
          log.warn('[DataspaceTreeStore] 🔁 Deduplicated', canonicalEdges.length, 'canonical edges for', childId, '- kept edge with parentId:', sorted[0].parentId);
        } else {
          // Single canonical edge - keep it
          deduplicatedEdges.push(...canonicalEdges);
        }
        
        // Always keep alias edges (future feature)
        if (aliasEdges.length > 0) {
          deduplicatedEdges.push(...aliasEdges);
        }
      }
    });
    
    if (duplicateCount > 0) {
      log.warn('[DataspaceTreeStore] ⚠️ Found and resolved', duplicateCount, 'duplicate edges');
    }
    
    const nodesById: Record<string, TreeNode> = {};
    
    // Build nodes from deduplicated edges
    deduplicatedEdges.forEach((edge: DataspaceEdge) => {
      // ✅ CRITICAL FIX: Use getEdgeKey to generate consistent key format
      const edgeKey = getEdgeKey(edge.childId, edge.parentId);
      nodesById[edgeKey] = {
        id: edge.childId,
        parentId: edge.parentId,
        pos: edge.pos,
        expanded: edge.expanded,
        edgeId: edge.id,
        kind: edge.kind
      };
    });
    
    log.log('[DataspaceTreeStore] Built', Object.keys(nodesById).length, 'nodes from edges');
    
    // ✅ CHECK: Entities without edges (new entities since last sync OR legacy entities)
    const entityStore = useEntityStore.getState();
    
    // ✅ CRITICAL: Don't filter by spaceId - dataspace tree shows ALL entities in dataspace
    // The SpaceSidebarV2 component will filter by space when displaying
    const allEntities = entityStore.entities
      .filter(isDisplayableEntity);
    
    log.log('[DataspaceTreeStore] 📦 Total displayable entities:', allEntities.length);
    
    const entitiesWithoutEdges = allEntities.filter((e: BaseEntityType) => {
      // Check if entity already has an edge (any edge with this childId)
      const hasEdge = Object.values(nodesById).some((node: TreeNode) => node.id === e._id);
      return !hasEdge;
    });
    
    log.log('[DataspaceTreeStore] 🆕 Entities WITHOUT edges:', entitiesWithoutEdges.length);
    
    if (entitiesWithoutEdges.length > 0) {
      log.log('[DataspaceTreeStore] 📝 Found', entitiesWithoutEdges.length, 'entities without edges, creating...');
      
      // Group entities by their target parent (from groupIds if legacy, or root)
      const entitiesByParent = new Map<string | null, BaseEntityType[]>();
      
      entitiesWithoutEdges.forEach((entity: BaseEntityType) => {
        // ✅ LEGACY FALLBACK: Try to read groupIds for old entities (read-only, won't write back)
        const groupIds = (entity.skeleton as any)?.groupIds || [];
        const legacyParent = groupIds.length > 0 ? groupIds[0] : null;
        
        if (legacyParent) {
          // ✅ Validate parent is a Group and actually exists
          const parentEntity = allEntities.find(e => e._id === legacyParent);
          const isValidParent = parentEntity && parentEntity.entityType === 'Group';
          
          if (isValidParent) {
            // Legacy entity with valid parent in groupIds
            const existing = entitiesByParent.get(legacyParent) || [];
            existing.push(entity);
            entitiesByParent.set(legacyParent, existing);
            log.log('[DataspaceTreeStore] 🔄 LEGACY RESCUE:', entity._id, '→', legacyParent, '(from groupIds)');
          } else {
            // Invalid parent (not a Group or doesn't exist) → root
            const existing = entitiesByParent.get(null) || [];
            existing.push(entity);
            entitiesByParent.set(null, existing);
            if (parentEntity) {
              log.warn('[DataspaceTreeStore] ⚠️ LEGACY RESCUE FAILED: Invalid parent type', parentEntity.entityType, '→ defaulting to root');
            } else {
              log.warn('[DataspaceTreeStore] ⚠️ LEGACY RESCUE FAILED: Parent not found', legacyParent, '→ defaulting to root');
            }
          }
        } else {
          // New entity or no parent → root
          const existing = entitiesByParent.get(null) || [];
          existing.push(entity);
          entitiesByParent.set(null, existing);
        }
      });
      
      // Create edges for each parent group
      entitiesByParent.forEach((entitiesToAdd, parentId) => {
        // Find max position in this parent to append new entities
        const siblingNodes = Object.values(nodesById).filter((n: TreeNode) => n.parentId === parentId);
        const siblingPositions = siblingNodes.map((n: TreeNode) => n.pos).sort();
        const lastPos = siblingPositions.length > 0 ? siblingPositions[siblingPositions.length - 1] : null;
        
        let currentPos = lastPos;
        
        // Create nodes for entities
        entitiesToAdd.forEach((entity: BaseEntityType) => {
          const newPos = generatePositionBetween(currentPos, null);
          const edgeKey = getEdgeKey(entity._id, parentId);
          nodesById[edgeKey] = {
            id: entity._id,
            parentId: parentId,
            pos: newPos,
            expanded: false,
            kind: 'canonical'
          };
          currentPos = newPos;
          
          // Queue edge creation
          get()._applyAddNode(edgeKey, nodesById[edgeKey], {
            type: 'create',
            nodeId: entity._id,
            parentId: parentId,
            pos: newPos,
            kind: 'canonical',
            timestamp: Date.now()
          });
        });
      });
      
      log.log('[DataspaceTreeStore] ✅ Created nodes for', entitiesWithoutEdges.length, 'entities, scheduling flush');
      
      // Flush immediately to persist new edges
      setTimeout(() => get()._scheduleFlush(true), 0);
    }
    
    set({ 
      nodesById,
      rebuildCount: get().rebuildCount + 1
    });
    
    get()._rebuildChildrenMap();
    
    log.log('[DataspaceTreeStore] ✅ Tree built with', Object.keys(nodesById).length, 'nodes');
  },
  
  // ========================================================================
  // Tree Operations (same API as spaceTreeStore)
  // ========================================================================
  
  moveNode: (nodeId: string, newParentId: string | null, afterNodeId: string | null, fromParentId?: string | null) => {
    log.log('[DataspaceTreeStore] 🎯 moveNode:', { 
      nodeId: nodeId.slice(-4), 
      to: newParentId?.slice(-4), 
      afterNodeId: afterNodeId?.slice(-4)
    });
    
    // ========================================================================
    // Execute atomic state update (ALL reads, calculations, and writes in ONE transaction)
    // ========================================================================
    let success = false;
    
    set(state => {
      const { nodesById, childrenByParentId, pendingOps, rebuildCount } = state;
      
      // ✅ PHASE 1: Find the edge to move (using consistent state)
      const oldEdgeKey = fromParentId !== undefined 
        ? getEdgeKey(nodeId, fromParentId)
        : Object.keys(nodesById).find(key => nodesById[key]?.id === nodeId);
      
      if (!oldEdgeKey) {
        log.warn('[DataspaceTreeStore] moveNode: node not found:', nodeId);
        console.error('⚠️ Failed to move item - node not found');
        return state; // No changes
      }
      
      const node = nodesById[oldEdgeKey];
      
      if (!node) {
        // This can happen when fromParentId doesn't match the actual parentId in the tree.
        // E.g. entity has parentId=null ('ROOT:id' key) but fromParentId='spaceId' ('spaceId:id' key).
        console.error('[DataspaceTreeStore] moveNode: edge not found. oldEdgeKey:', oldEdgeKey,
          '| fromParentId:', fromParentId, '| node IDs in store with this id:',
          Object.entries(nodesById).filter(([, n]) => n.id === nodeId).map(([k]) => k));
        return state; // No changes
      }
      
      // ✅ PHASE 2: Calculate new position (using consistent state)
      const siblings = (childrenByParentId.get(newParentId) || []).filter(id => id !== nodeId);
      const afterIndex = afterNodeId ? siblings.indexOf(afterNodeId) : -1;
      
      // Helper to get sibling position from current transaction state
      const getSiblingPos = (siblingId: string, parentId: string | null): string | null => {
        const newFormatKey = getEdgeKey(siblingId, parentId);
        let pos = nodesById[newFormatKey]?.pos;
        
        // Fallback to old format
        if (!pos) {
          pos = nodesById[siblingId]?.pos;
        }
        
        return pos || null;
      };
      
      let newPos: string;
      if (afterIndex === -1) {
        // Insert at beginning
        const firstSiblingPos = siblings.length > 0 ? getSiblingPos(siblings[0], newParentId) : null;
        newPos = generatePositionBetween(null, firstSiblingPos);
      } else if (afterIndex === siblings.length - 1) {
        // Insert at end
        const lastSiblingPos = getSiblingPos(siblings[afterIndex], newParentId);
        newPos = generatePositionBetween(lastSiblingPos, null);
      } else {
        // Insert in middle
        const prevPos = getSiblingPos(siblings[afterIndex], newParentId);
        const nextPos = getSiblingPos(siblings[afterIndex + 1], newParentId);
        newPos = generatePositionBetween(prevPos, nextPos);
      }
      
      // ✅ BUG FIX #3: Validate position was successfully calculated
      if (!newPos || newPos === '') {
        log.error('[DataspaceTreeStore] Position calculation failed, using default');
        newPos = generatePositionBetween(null, null);
      }
      
      // ✅ PHASE 3: Build new state
      const correctOldEdgeKey = getEdgeKey(nodeId, node.parentId);
      const correctNewEdgeKey = getEdgeKey(nodeId, newParentId);
      const isSameParent = node.parentId === newParentId;
      
      // Build new nodesById
      const newNodesById = { ...nodesById };
      
      // Remove old edge(s)
      if (oldEdgeKey !== correctOldEdgeKey) {
        delete newNodesById[oldEdgeKey];
      }
      if (!isSameParent) {
        delete newNodesById[correctOldEdgeKey];
      }
      
      // Add updated edge
      const updatedNode = { ...node, parentId: newParentId, pos: newPos };
      const targetKey = isSameParent ? correctOldEdgeKey : correctNewEdgeKey;
      newNodesById[targetKey] = updatedNode;
      
      // Build new pendingOps
      let newPendingOps = [...pendingOps];
      
      if (isSameParent) {
        // Reorder operation
        newPendingOps.push({
          type: 'move',
          nodeId,
          parentId: newParentId,
          pos: newPos,
          edgeId: node.edgeId,
          timestamp: Date.now()
        });
      } else {
        // Move to different parent: delete old + create new
        newPendingOps.push({
          type: 'delete',
          edgeId: node.edgeId,
          nodeId: node.id,
          parentId: node.parentId,
          timestamp: Date.now()
        });
        
        newPendingOps.push({
          type: 'create',
          nodeId,
          parentId: newParentId,
          pos: newPos,
          kind: node.kind || 'canonical',
          timestamp: Date.now()
        });
      }
      
      success = true;
      
      return {
        nodesById: newNodesById,
        pendingOps: newPendingOps,
        rebuildCount: rebuildCount + 1
      };
    });
    
    // ========================================================================
    // Side effects (after atomic update completes)
    // ========================================================================
    if (success) {
      get()._rebuildChildrenMap();
      get()._scheduleFlush();
      log.log('[DataspaceTreeStore] ✅ moveNode complete');

      // Write updated edge to the org Y.Doc so other clients see the move instantly.
      const pendingOps = get().pendingOps;
      const lastOp = pendingOps[pendingOps.length - 1];
      if (lastOp?.type === 'move' && lastOp.edgeId) {
        import('./orgYDocStore').then(({ writeEdgeToOrgYDoc }) => {
          const node = get().nodesById[getEdgeKey(lastOp.nodeId, lastOp.parentId)];
          if (node) {
            writeEdgeToOrgYDoc(lastOp.edgeId!, {
              parentId: lastOp.parentId,
              childId: lastOp.nodeId,
              pos: lastOp.pos,
              expanded: node.expanded,
              kind: node.kind,
            });
          }
        }).catch(() => {});
      }
    } else {
      // Auto-recovery attempt
      setTimeout(() => {
        log.log('[DataspaceTreeStore] 🔄 Attempting auto-recovery via refreshEdges()');
        get().refreshEdges().catch(err => {
          console.error('[DataspaceTreeStore] ❌ Auto-recovery failed:', err);
        });
      }, 1000);
    }
  },
  
  toggleExpanded: (nodeId: string, parentId?: string | null) => {
    // ✅ ATOMIC UPDATE: Toggle all edges for this node in one transaction
    log.log('[DataspaceTreeStore] toggleExpanded called for nodeId:', nodeId);
    let hasChanges = false;
    
    set(state => {
      const { nodesById, pendingOps, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      
      log.log('[DataspaceTreeStore]   Found matching edge keys:', matchingEdgeKeys);
      log.log('[DataspaceTreeStore]   Current states:', 
        matchingEdgeKeys.map(key => ({ 
          key: key.slice(-16), 
          expanded: nodesById[key].expanded 
        }))
      );
      
      if (matchingEdgeKeys.length === 0) {
        console.warn('[DataspaceTreeStore]   ⚠️ NO MATCHING EDGES FOUND for nodeId:', nodeId);
        return state;
      }
      
      const newNodesById = { ...nodesById };
      const newPendingOps = [...pendingOps];
      
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        const newExpanded = !node.expanded;
        
        log.log('[DataspaceTreeStore]   Toggling edge:', {
          key: edgeKey.slice(-16),
          from: node.expanded,
          to: newExpanded
        });
        
        newNodesById[edgeKey] = { ...node, expanded: newExpanded };
        newPendingOps.push({
          type: 'expand',
          nodeId,
          expanded: newExpanded,
          edgeId: node.edgeId,
          timestamp: Date.now()
        });
        hasChanges = true;  // ✅ BUG FIX #2: Set inside loop, not after
      });
      
      if (!hasChanges) return state;  // ✅ Early return if no changes
      
      log.log('[DataspaceTreeStore]   ✅ Toggle complete, new rebuildCount:', rebuildCount + 1);
      
      return {
        nodesById: newNodesById,
        pendingOps: newPendingOps,
        rebuildCount: rebuildCount + 1
      };
    });
    
    if (hasChanges) {
      get()._rebuildChildrenMap();
      get()._scheduleFlush();
    }
  },
  
  expandNode: (nodeId: string, parentId?: string | null) => {
    // ✅ ATOMIC UPDATE: Expand all edges for this node in one transaction
    let hasChanges = false;
    
    set(state => {
      const { nodesById, pendingOps, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      
      const newNodesById = { ...nodesById };
      const newPendingOps = [...pendingOps];
      
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (node.expanded) return; // Already expanded
        
        newNodesById[edgeKey] = { ...node, expanded: true };
        newPendingOps.push({
          type: 'expand',
          nodeId,
          expanded: true,
          edgeId: node.edgeId,
          timestamp: Date.now()
        });
        hasChanges = true;
      });
      
      if (!hasChanges) return state;
      
      return {
        nodesById: newNodesById,
        pendingOps: newPendingOps,
        rebuildCount: rebuildCount + 1
      };
    });
    
    if (hasChanges) {
      get()._rebuildChildrenMap();
      get()._scheduleFlush();
    }
  },
  
  collapseNode: (nodeId: string, parentId?: string | null) => {
    // ✅ ATOMIC UPDATE: Collapse all edges for this node in one transaction
    let hasChanges = false;
    
    set(state => {
      const { nodesById, pendingOps, rebuildCount } = state;
      const matchingEdgeKeys = Object.keys(nodesById).filter(key => nodesById[key].id === nodeId);
      
      const newNodesById = { ...nodesById };
      const newPendingOps = [...pendingOps];
      
      matchingEdgeKeys.forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (!node.expanded) return; // Already collapsed
        
        newNodesById[edgeKey] = { ...node, expanded: false };
        newPendingOps.push({
          type: 'expand',
          nodeId,
          expanded: false,
          edgeId: node.edgeId,
          timestamp: Date.now()
        });
        hasChanges = true;
      });
      
      if (!hasChanges) return state;
      
      return {
        nodesById: newNodesById,
        pendingOps: newPendingOps,
        rebuildCount: rebuildCount + 1
      };
    });
    
    if (hasChanges) {
      get()._rebuildChildrenMap();
      get()._scheduleFlush();
    }
  },
  
  expandAllNodes: (): { count: number } => {
    let count = 0;

    set(state => {
      const { nodesById, pendingOps, rebuildCount } = state;
      const newNodesById = { ...nodesById };
      const newPendingOps = [...pendingOps];

      Object.keys(nodesById).forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: true };
        newPendingOps.push({
          type: 'expand',
          nodeId: node.id,
          expanded: true,
          edgeId: node.edgeId,
          timestamp: Date.now()
        });
        count++;
      });

      if (count === 0) return state;
      return { nodesById: newNodesById, pendingOps: newPendingOps, rebuildCount: rebuildCount + 1 };
    });

    if (count > 0) {
      get()._rebuildChildrenMap();
      get()._scheduleFlush();
    }

    log.log(`[DataspaceTreeStore] expandAllNodes: expanded ${count} nodes`);
    return { count };
  },

  collapseAllNodes: (): { count: number } => {
    let count = 0;

    set(state => {
      const { nodesById, pendingOps, rebuildCount } = state;
      const newNodesById = { ...nodesById };
      const newPendingOps = [...pendingOps];

      Object.keys(nodesById).forEach(edgeKey => {
        const node = nodesById[edgeKey];
        if (!node.expanded) return;
        newNodesById[edgeKey] = { ...node, expanded: false };
        newPendingOps.push({
          type: 'expand',
          nodeId: node.id,
          expanded: false,
          edgeId: node.edgeId,
          timestamp: Date.now()
        });
        count++;
      });

      if (count === 0) return state;
      return { nodesById: newNodesById, pendingOps: newPendingOps, rebuildCount: rebuildCount + 1 };
    });

    if (count > 0) {
      get()._rebuildChildrenMap();
      get()._scheduleFlush();
    }

    log.log(`[DataspaceTreeStore] collapseAllNodes: collapsed ${count} nodes`);
    return { count };
  },

  // ✅ Add alias edge to an entity (creates a second reference to the same entity)
  addAliasEdge: async (entityId: string, parentId: string | null) => {
    const { dataspaceId, initialized } = get();
    
    if (!initialized || !dataspaceId) {
      log.error('[addAliasEdge] Tree not initialized');
      return;
    }
    
    // Check if edge already exists
    const edgeKey = getEdgeKey(entityId, parentId);
    if (get().nodesById[edgeKey]) {
      log.log('[addAliasEdge] Edge already exists:', edgeKey);
      return;
    }
    
    log.log('[addAliasEdge] 📝 Creating alias edge:', {
      entityId: entityId.slice(-8),
      parentId: parentId?.slice(-8) || 'null',
      edgeKey
    });
    
    // Calculate position
    const siblings = get().getChildNodeIds(parentId);
    const lastSiblingEdgeKey = siblings.length > 0 ? getEdgeKey(siblings[siblings.length - 1], parentId) : null;
    const lastSiblingPos = lastSiblingEdgeKey ? get().nodesById[lastSiblingEdgeKey]?.pos : null;
    const pos = generatePositionBetween(lastSiblingPos, null);
    
    // Create edge in backend first
    try {
      const response = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges`, {
        method: 'POST',
        body: JSON.stringify({
          parentId,
          childId: entityId,
          pos,
          expanded: false,
          kind: 'alias'
        }),
        credentials: 'include'
      });
      
      log.log('[addAliasEdge] ✅ Alias edge created in backend:', response);
      
      // Add to local tree
      const newNode: TreeNode = {
        id: entityId,
        parentId,
        pos,
        expanded: false,
        edgeId: response.id,
        kind: 'alias'
      };
      
      get()._applyAddNode(edgeKey, newNode, undefined); // No operation to avoid double persistence
      log.log('[addAliasEdge] ✅ Alias edge added to local tree');
      
    } catch (error) {
      log.error('[addAliasEdge] Failed to create alias edge:', error);
      throw error;
    }
  },
  
  ensureNodeExists: (nodeId: string, parentId: string | null, kind: 'canonical' | 'alias' = 'canonical') => {
    const { nodesById, initialized, dataspaceId } = get();
    
    // ✅ GUARD: Don't create nodes if tree not initialized
    // This prevents edge creation failures during app startup or tree switching
    if (!initialized || !dataspaceId) {
      log.log('[DataspaceTreeStore] Tree not initialized, skipping node creation:', {
        nodeId: nodeId.slice(-8),
        parentId: parentId?.slice(-8) || 'null',
        initialized,
        hasDataspaceId: !!dataspaceId
      });
      return;
    }
    
    // ✅ ALIAS SUPPORT: Check if THIS SPECIFIC EDGE exists (not just if entity exists)
    const edgeKey = getEdgeKey(nodeId, parentId);
    if (nodesById[edgeKey]) {
      log.log('[DataspaceTreeStore] Edge already exists:', { edgeKey, nodeId: nodeId.slice(-8), parentId: parentId?.slice(-8) });
      return;
    }
    
    // Create node with default position
    const siblings = get().getChildNodeIds(parentId);
    const lastSiblingPos = siblings.length > 0 ? nodesById[getEdgeKey(siblings[siblings.length - 1], parentId)]?.pos : null;
    const pos = generatePositionBetween(lastSiblingPos, null);
    
    const newNode: TreeNode = {
      id: nodeId,
      parentId,
      pos,
      expanded: false,
      kind
    };
    
    log.log('[DataspaceTreeStore] 📝 Creating new edge:', {
      edgeKey,
      nodeId: nodeId.slice(-8),
      parentId: parentId?.slice(-8) || 'null',
      pos,
      kind
    });
    
    get()._applyAddNode(edgeKey, newNode, {
      type: 'create',
      nodeId,
      parentId,
      pos,
      kind,
      timestamp: Date.now()
    });
  },
  
  // ========================================================================
  // Queries (O(1) lookups)
  // ========================================================================
  
  getRootNodeIds: () => {
    const { childrenByParentId } = get();
    return childrenByParentId.get(null) || [];
  },
  
  getChildNodeIds: (parentId: string | null) => {
    const { childrenByParentId } = get();
    const children = childrenByParentId.get(parentId) || [];
    
    // ✅ DEFENSIVE FALLBACK: If getting SPACE root children, also include orphaned nodes (parent_id: null)
    // This prevents entities from disappearing when edges are malformed
    // The repair mechanism will fix these on next workspace load
    // 🔧 FIX: Only apply fallback for SPACES, not all entities!
    if (parentId !== null) {
      // Check if parentId is a Space entity
      const entityStore = useEntityStore.getState();
      const parentEntity = entityStore.entities.find((e: any) => e._id === parentId);
      const isSpace = parentEntity?.entityType === 'Space';
      
      // Only include orphans if this is a space root
      if (isSpace) {
        const orphanedChildren = childrenByParentId.get(null) || [];
        
        if (orphanedChildren.length > 0) {
          // Only include orphans that should be in THIS space (not other dataspaces)
          const relevantOrphans = orphanedChildren.filter(childId => {
            const entity = entityStore.entities.find((e: any) => e._id === childId);
            // Include if entity belongs to this space OR has no spaceId (truly orphaned)
            return entity && (entity.spaceId === parentId || !entity.spaceId);
          });
          
          if (relevantOrphans.length > 0) {
            // Fallback is working - orphaned nodes being included (log disabled to reduce spam)
            // log.log(`[DataspaceTreeStore] 🛡️ FALLBACK: Including ${relevantOrphans.length} orphaned nodes at space root (parent_id: null)`);
            // Combine and deduplicate
            return [...new Set([...children, ...relevantOrphans])];
          }
        }
      }
    }
    
    return children;
  },
  
  getNode: (nodeId: string, parentId?: string | null) => {
    // ✅ ALIAS SUPPORT: If parentId provided, look up specific edge; otherwise find any edge for this entity
    if (parentId !== undefined) {
      const edgeKey = getEdgeKey(nodeId, parentId);
      return get().nodesById[edgeKey];
    }
    // Find first node for this entity (backward compatibility)
    const nodesById = get().nodesById;
    return Object.values(nodesById).find(n => n.id === nodeId);
  },
  
  // ========================================================================
  // Safe Accessor Methods - Prevent nodesById Key Format Bugs
  // ========================================================================
  
  /**
   * Get a node by edge (childId + parentId)
   * ✅ SAFE: Encapsulates edge key format
   */
  getNodeByEdgeKey: (childId: string, parentId: string | null) => {
    const edgeKey = getEdgeKey(childId, parentId);
    return get().nodesById[edgeKey];
  },
  
  /**
   * Get all nodes (edges) for an entity (supports aliases - same entity in multiple locations)
   * ✅ SAFE: Returns all edges, not just first one
   */
  getNodesByEntityId: (entityId: string) => {
    const { nodesById } = get();
    return Object.keys(nodesById)
      .filter(key => nodesById[key].id === entityId)
      .map(key => nodesById[key]);
  },
  
  /**
   * Check if a specific edge exists
   * ✅ SAFE: Encapsulates edge key format
   */
  hasEdge: (childId: string, parentId: string | null) => {
    const edgeKey = getEdgeKey(childId, parentId);
    return !!get().nodesById[edgeKey];
  },
  
  /**
   * Get expanded state for an entity
   * ✅ SAFE: Handles multiple edges (returns true if ANY edge is expanded)
   */
  getExpandedState: (entityId: string) => {
    const { nodesById } = get();
    const matchingNodes = Object.keys(nodesById)
      .filter(key => nodesById[key].id === entityId)
      .map(key => nodesById[key]);
    return matchingNodes.some((node: TreeNode) => node.expanded);
  },
  
  // ========================================================================
  // Legacy Methods (for backward compatibility)
  // ========================================================================
  
  isExpanded: (nodeId: string, parentId?: string | null) => {
    const node = get().getNode(nodeId, parentId);
    return node?.expanded ?? false;
  },
  
  hasChildren: (nodeId: string) => {
    return get().getChildNodeIds(nodeId).length > 0;
  },
  
  // ========================================================================
  // Internal Mutations
  // ========================================================================
  
  _applyAddNode: (edgeKey: string, node: TreeNode, operation?: TreeOperation) => {
    if (!operation) {
      // Remote add (from org Y.Doc WS) — always log so we can trace sidebar update triggers
      console.log(`[TreeStore] _applyAddNode (remote): key=${edgeKey} child=${node.id.slice(-8)} parent=${node.parentId?.slice(-8) ?? 'root'}`)
    }
    if (VERBOSE_LOGGING) {
      log.log('[_applyAddNode] 📝 Adding node:', {
        edgeKey,
        nodeId: node.id.slice(-8),
        parentId: node.parentId?.slice(-8) || 'null',
        pos: node.pos,
        kind: node.kind,
        hasOperation: !!operation,
        operationType: operation?.type
      });
    }
    
    set(state => {
      const newNodesById = { ...state.nodesById, [edgeKey]: node };
      const newPendingOps = operation ? [...state.pendingOps, operation] : state.pendingOps;
      
      return {
        nodesById: newNodesById,
        pendingOps: newPendingOps,
        rebuildCount: state.rebuildCount + 1
      };
    });
    
    get()._rebuildChildrenMap();
    
    if (VERBOSE_LOGGING) {
      const parentChildren = get().getChildNodeIds(node.parentId);
      log.log('[_applyAddNode] ✅ Node added, parent now has children:', {
        edgeKey,
        nodeId: node.id.slice(-8),
        parentId: node.parentId?.slice(-8) || 'null',
        parentChildrenCount: parentChildren.length,
        rebuildCount: get().rebuildCount
      });
    }
    
    if (operation) {
      get()._scheduleFlush();
    }
  },
  
  _applyRemoveNode: (edgeKey: string, options: { persist?: boolean } = {}) => {
    // Read node first to check existence before entering the set() updater
    const node = get().nodesById[edgeKey];
    if (!node) {
      log.warn('[DataspaceTreeStore] _applyRemoveNode: node not found for key:', edgeKey.slice(-8));
      return;
    }
    
    log.log('[DataspaceTreeStore] 🗑️ Removing edge:', { edgeKey, nodeId: node.id.slice(-8) });

    // Use set(state => ...) for an atomic read-modify-write so that any concurrent
    // _applyAddNode calls that run between our read and write are not lost.
    set(state => {
      if (!state.nodesById[edgeKey]) return state; // already removed

      const newNodesById = { ...state.nodesById };
      delete newNodesById[edgeKey];

      const deleteOp = options.persist
        ? [{
            type: 'delete' as const,
            edgeId: node.edgeId,
            nodeId: node.id,
            parentId: node.parentId,
            timestamp: Date.now(),
          }]
        : [];

      return {
        nodesById: newNodesById,
        pendingOps: [...state.pendingOps, ...deleteOp],
        rebuildCount: state.rebuildCount + 1,
      };
    });
    
    get()._rebuildChildrenMap();
    
    if (options.persist) {
      get()._scheduleFlush();
    }
  },
  
  _rebuildChildrenMap: () => {
    const startTime = performance.now();
    const { nodesById } = get();
    const newMap = new Map<string | null, string[]>();
    
    // ⚡ PERFORMANCE FIX: Precompute position map BEFORE sorting
    // Keyed by edgeKey (parentId:entityId) so alias edges with different parents
    // never collide — each (child, parent) pair has its own pos.
    const posMap = new Map<string, string>();
    
    // ✅ ALIAS SUPPORT: Build map from edge nodes (keyed by edgeKey)
    // Each node represents an edge (parent -> child relationship)
    Object.entries(nodesById).forEach(([edgeKey, node]) => {
      posMap.set(edgeKey, node.pos);
      const existing = newMap.get(node.parentId) || [];
      // Only add if not already in list (de-duplicate)
      if (!existing.includes(node.id)) {
        newMap.set(node.parentId, [...existing, node.id]);
      }
    });

    // Cycle detection: walk from each root and warn on back-edges
    const hasCycle = (() => {
      const visited = new Set<string>();
      const inStack = new Set<string>();
      const dfs = (id: string): boolean => {
        if (inStack.has(id)) return true;
        if (visited.has(id)) return false;
        visited.add(id);
        inStack.add(id);
        for (const childId of newMap.get(id) || []) {
          if (dfs(childId)) {
            console.warn('[DataspaceTreeStore] ⚠️ Cycle detected in tree: edge', id, '→', childId);
            return true;
          }
        }
        inStack.delete(id);
        return false;
      };
      for (const id of Array.from(new Set(Array.from(newMap.values()).flat()))) {
        if (!visited.has(id) && dfs(id)) return true;
      }
      return false;
    })();

    if (hasCycle) {
      console.error('[DataspaceTreeStore] ❌ Tree contains cycles! This will cause infinite recursion when rendering expanded nodes.');
    }
    
    // ⚡ OPTIMIZED: Sort using precomputed position map - O(m log m) instead of O(m² × n)
    newMap.forEach((children, parentId) => {
      children.sort((a, b) => {
        // ✅ O(1) map lookup keyed by edgeKey so alias edges don't collide
        const posA = posMap.get(getEdgeKey(a, parentId)) || '';
        const posB = posMap.get(getEdgeKey(b, parentId)) || '';
        return posA.localeCompare(posB);
      });
    });
    
    const elapsed = performance.now() - startTime;
    
    if (VERBOSE_LOGGING) {
      log.log('[_rebuildChildrenMap] 🔄 Rebuilt children map:', {
        totalParents: newMap.size,
        totalEdges: Object.keys(nodesById).length,
        rebuildTime: `${elapsed.toFixed(2)}ms`,
        sampleParents: Array.from(newMap.entries()).slice(0, 3).map(([p, c]) => ({
          parent: p?.slice(-8) || 'null',
          childCount: c.length
        }))
      });
    }
    
    // Log performance warning if rebuild is slow
    if (elapsed > 10) {
      console.warn(`[DataspaceTreeStore] ⚠️ Slow rebuild: ${elapsed.toFixed(2)}ms for ${Object.keys(nodesById).length} nodes`);
    }
    
    set({ childrenByParentId: newMap     });
  },
  
  // ========================================================================
  // Persistence
  // ========================================================================
  
  _scheduleFlush: (immediate = false) => {
    const { pendingOps, lastFlushTime, flushTimeoutId } = get();
    
    if (pendingOps.length === 0) return;
    
    // ✅ CRITICAL FIX: Clear any existing timeout to prevent race conditions
    // Without this, rapid calls create multiple timeouts that all fire
    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId);
      log.log('[DataspaceTreeStore] Cleared previous flush timeout to prevent race');
    }
    
    const now = Date.now();
    const timeSinceLastFlush = now - lastFlushTime;
    
    if (immediate || timeSinceLastFlush > 2000) {
      // Flush immediately (but still async to avoid blocking)
      log.log('[DataspaceTreeStore] Scheduling immediate flush');
      const timeoutId = setTimeout(() => {
        set({ flushTimeoutId: null });  // Clear after firing
        get()._flushToBackend();
      }, 0);
      set({ flushTimeoutId: timeoutId });
    } else {
      // Debounce (100ms)
      log.log('[DataspaceTreeStore] Scheduling debounced flush (100ms)');
      const timeoutId = setTimeout(() => {
        set({ flushTimeoutId: null });  // Clear after firing
        const { pendingOps: currentOps } = get();
        if (currentOps.length > 0) {
          get()._flushToBackend();
        }
      }, 100);
      set({ flushTimeoutId: timeoutId });
    }
  },
  
  _flushToBackend: async () => {
    // Mutex: prevent concurrent flush calls from duplicating ops
    if (get().isFlushInProgress) {
      log.log('[DataspaceTreeStore] Flush already in progress, skipping');
      return;
    }

    const { dataspaceId, pendingOps } = get();
    
    if (!dataspaceId || pendingOps.length === 0) return;
    
    set({ isFlushInProgress: true });
    
    log.log('[DataspaceTreeStore] 💾 Flushing', pendingOps.length, 'operations to backend');
    log.log('[DataspaceTreeStore] 🚀 FLUSHING TO BACKEND:', {
      totalOps: pendingOps.length,
      dataspaceId: dataspaceId.slice(-6),
      operations: pendingOps.map(op => {
        const opDetails: any = { type: op.type };
        if ('nodeId' in op) opDetails.nodeId = op.nodeId?.slice(-6);
        if ('parentId' in op) opDetails.parentId = op.parentId?.slice(-6) || 'ROOT';
        if ('edgeId' in op) opDetails.edgeId = op.edgeId?.slice(-6) || 'NONE';
        return opDetails;
      })
    });
    
    const opsToFlush = [...pendingOps];
    // ✅ CRITICAL FIX: Don't clear pendingOps until AFTER successful flush (line ~1679)
    // This prevents data loss if network error occurs before try block completes
    
    try {
      // Group operations by type
      const creates: TreeOperation[] = [];
      const moves: TreeOperation[] = [];
      const expands: TreeOperation[] = [];
      const deletes: TreeOperation[] = [];
      
      opsToFlush.forEach(op => {
        if (op.type === 'create') creates.push(op);
        else if (op.type === 'move') moves.push(op);
        else if (op.type === 'expand') expands.push(op);
        else if (op.type === 'delete') deletes.push(op);
      });
      
      log.log('[DataspaceTreeStore] 📊 Operations grouped:', {
        creates: creates.length,
        moves: moves.length,
        expands: expands.length,
        deletes: deletes.length
      });
      
      // Process creates (new edges)
      if (creates.length > 0) {
        log.log('[DataspaceTreeStore] Creating', creates.length, 'new edges');
        log.log('[DataspaceTreeStore] ➕ CREATING EDGES:', creates.map(op => {
          const opDetails: any = {};
          if ('nodeId' in op) opDetails.nodeId = op.nodeId?.slice(-6);
          if ('parentId' in op) opDetails.parentId = op.parentId?.slice(-6) || 'ROOT';
          if ('pos' in op) opDetails.pos = op.pos;
          return opDetails;
        }));
        
        const edgeRequests = creates.map(op => {
          if (op.type !== 'create') return null;
          return {
            parentId: op.parentId,
            childId: op.nodeId,
            pos: op.pos,
            expanded: false,
            kind: op.kind || 'canonical'
          };
        }).filter(Boolean);
        
        const createdEdges: DataspaceEdge[] = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/batch`, {
          method: 'POST',
          body: JSON.stringify({ edges: edgeRequests }),
          credentials: 'include',
        });
        
        log.log('[DataspaceTreeStore] ✅ EDGES CREATED:', createdEdges.map((e: DataspaceEdge) => ({
          id: e.id?.slice(-6),
          childId: e.childId?.slice(-6),
          parentId: e.parentId?.slice(-6) || 'ROOT'
        })));
        
        // Update nodes with edge IDs
        set(state => {
          const newNodesById = { ...state.nodesById };
          createdEdges.forEach((edge: DataspaceEdge) => {
            const edgeKey = getEdgeKey(edge.childId, edge.parentId);
            if (newNodesById[edgeKey]) {
              newNodesById[edgeKey] = {
                ...newNodesById[edgeKey],
                edgeId: edge.id
              };
              log.log(`[DataspaceTreeStore] 🔄 Updated edgeId for ${edgeKey}: ${edge.id?.slice(-6)}`);
            }
          });
          return { nodesById: newNodesById };
        });
      }
      
      // Process moves (update edges)
      if (moves.length > 0) {
        log.log('[DataspaceTreeStore] Moving', moves.length, 'nodes');
        
        for (const op of moves) {
          if (op.type !== 'move') continue;
          
          if (!op.edgeId) {
            log.warn('[DataspaceTreeStore] Move operation missing edgeId:', op.nodeId);
            continue;
          }
          
          try {
            await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/${op.edgeId}`, {
              method: 'PATCH',
              body: JSON.stringify({
                parentId: op.parentId,
                pos: op.pos
              }),
              credentials: 'include',
            });
          } catch (error) {
            log.error('[DataspaceTreeStore] Failed to move node:', op.nodeId, error);
          }
        }
      }
      
      // Process expansion changes
      if (expands.length > 0) {
        log.log('[DataspaceTreeStore] Updating', expands.length, 'expansion states');
        
        for (const op of expands) {
          if (op.type !== 'expand') continue;
          
          if (!op.edgeId) {
            log.warn('[DataspaceTreeStore] Expand operation missing edgeId:', op.nodeId);
            continue;
          }
          
          try {
            await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/${op.edgeId}`, {
              method: 'PATCH',
              body: JSON.stringify({
                expanded: op.expanded
              }),
              credentials: 'include',
            });
          } catch (error) {
            log.error('[DataspaceTreeStore] Failed to update expansion:', op.nodeId, error);
          }
        }
      }
      
      // Process deletes
      if (deletes.length > 0) {
        log.log('[DataspaceTreeStore] Deleting', deletes.length, 'edges');
        
        for (const op of deletes) {
          if (op.type !== 'delete') continue;
          
          try {
            if (op.edgeId) {
              // Delete by edge ID if available
              await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/${op.edgeId}`, {
                method: 'DELETE',
                credentials: 'include',
              });
            } else if (op.nodeId && op.parentId !== undefined) {
              // Delete by (child_id, parent_id) pair if no edgeId
              const params = new URLSearchParams({
                child_id: op.nodeId,
              });
              
              if (op.parentId !== null) {
                params.append('parent_id', op.parentId);
              } else {
                params.append('parent_id', 'null');
              }
              
              await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/by-pair?${params.toString()}`, {
                method: 'DELETE',
                credentials: 'include',
              });
              
              log.log('[DataspaceTreeStore]   Deleted edge by pair:', { nodeId: op.nodeId.slice(-6), parentId: op.parentId?.slice(-6) || 'null' });
            } else {
              log.warn('[DataspaceTreeStore]   Cannot delete edge - missing both edgeId and (nodeId, parentId) pair');
            }
          } catch (error) {
            log.error('[DataspaceTreeStore] Failed to delete edge:', op, error);
          }
        }
      }
      
      log.log('[DataspaceTreeStore] ✅ ALL OPERATIONS FLUSHED SUCCESSFULLY');
      log.log('[DataspaceTreeStore] ✅ Flush complete');
      
      // Remove only the ops we flushed by object reference identity.
      // Any new ops pushed into pendingOps during the async awaits above will not
      // be in flushedSet and are therefore preserved for the next flush.
      const flushedSet = new Set(opsToFlush);
      set(state => ({
        pendingOps: state.pendingOps.filter(op => !flushedSet.has(op)),
        lastFlushTime: Date.now(),
        isFlushInProgress: false,
      }));
      
    } catch (error) {
      // ✅ CRITICAL ERROR HANDLING: Network failure or backend error
      console.error('[DataspaceTreeStore] ❌ FLUSH FAILED:', error);
      log.error('[DataspaceTreeStore] ❌ Flush failed:', error);
      
      set({ isFlushInProgress: false });

      // Restore ops to queue for retry, deduplicating by object reference to avoid
      // doubling if new ops arrived during the failed flush
      const currentOps = get().pendingOps;
      const currentSet = new Set(currentOps);
      const missingOps = opsToFlush.filter(op => !currentSet.has(op));
      if (missingOps.length > 0) {
        set(state => ({ pendingOps: [...missingOps, ...state.pendingOps] }));
      }
      
      log.log('[DataspaceTreeStore] 🔄 Operations restored to queue, will retry in 5s');
      
      // ✅ SCHEDULE RETRY with exponential backoff (5s delay)
      setTimeout(() => {
        log.log('[DataspaceTreeStore] 🔄 Retrying failed flush...');
        get()._scheduleFlush(true);
      }, 5000);
      
      // ✅ USER FEEDBACK: Show error notification
      console.error('⚠️ [USER NOTIFICATION] Failed to save changes. Retrying automatically...');
      // TODO: Add toast.error() when toast library is available
    }
  },
  
   // ========================================================================
  // Refresh
  // ========================================================================
  
  /**
   * ✅ NEW: Refresh edges from backend after entity operations
   * Called after AI creates/moves entities to sync backend-created edges
   */
  refreshEdges: async (opts?: { fetchMissingEntities?: boolean }) => {
    const { dataspaceId } = get();
    console.log('[DataspaceTreeStore] refreshEdges called, dataspaceId:', dataspaceId?.slice(-8) ?? 'null')
    if (!dataspaceId) {
      log.warn('[DataspaceTreeStore] Cannot refresh - no dataspaceId');
      console.warn('[DataspaceTreeStore] ⚠️ refreshEdges: dataspaceId is null, cannot refresh')
      return;
    }
    
    try {
      log.info('[DataspaceTreeStore] 🔄 Refreshing edges from backend');
      console.log('[DataspaceTreeStore] 🔄 Fetching edges for dataspaceId:', dataspaceId.slice(-8))
      
      const url = `${API_BASE_URL}/dataspaces/${dataspaceId}/edges`;
      const response = await fetchService(url, {
        credentials: 'include',
      });
      
      if (!Array.isArray(response)) {
        throw new Error(`Expected array of edges, got ${typeof response}`);
      }
      
      const edges: DataspaceEdge[] = response;
      log.info('[DataspaceTreeStore] ✅ Edges refreshed from backend:', { 
        count: edges.length,
        dataspaceId: dataspaceId.slice(-8)
      });
      
      // Rebuild tree with fresh edges
      get().initFromEdges(edges);

      // Safety net: only runs when triggered by the cross-tab Y.Doc edge signal
      // (opts.fetchMissingEntities = true), NOT on every refreshEdges() call.
      // Fetching on every call would flood the API — the tree contains nodes for the
      // entire dataspace, while entityStore only holds entities for the current context.
      if (opts?.fetchMissingEntities) {
        const entityStoreState = useEntityStore.getState();
        const existingEntityIds = new Set(entityStoreState.entities.map((e: BaseEntityType) => e._id));

        // Fetch all entities in the tree that aren't in entityStore yet — this covers
        // nested entities (inside folders) as well as direct space children.
        const missingEntityIds = [...new Set(
          Object.values(get().nodesById)
            .filter((n: TreeNode) => n.parentId !== null && !existingEntityIds.has(n.id) && !n.id.startsWith('__'))
            .map((n: TreeNode) => n.id)
        )];

        if (missingEntityIds.length > 0) {
          log.info('[DataspaceTreeStore] 🔄 Fetching', missingEntityIds.length, 'missing entities from REST');
          await Promise.all(missingEntityIds.map(async (entityId) => {
            try {
              const entity = await fetchService(`${API_BASE_URL}/entities/${entityId}`, {
                credentials: 'include',
              });
              if (entity?._id) {
                useEntityStore.getState()._applyLocalUpsert(entity, { source: 'crdt', log: false, syncCRDT: false });
              }
            } catch (err: any) {
              const is404 = err?.status === 404 || err?.message?.includes('404') || err?.message?.includes('not found');
              if (is404) {
                // Entity no longer exists on the backend — orphaned edge. Delete it so
                // this 404 doesn't fire on every subsequent tree refresh.
                log.warn('[DataspaceTreeStore] 🧹 Removing orphaned edge for missing entity:', entityId.slice(-8));
                try {
                  const { dataspaceId: currentDataspaceId } = get();
                  if (currentDataspaceId) {
                    await fetchService(
                      `${API_BASE_URL}/dataspaces/${currentDataspaceId}/edges/by-child/${entityId}`,
                      { method: 'DELETE', credentials: 'include' }
                    );
                    log.info('[DataspaceTreeStore] ✅ Orphaned edge deleted from backend for:', entityId.slice(-8));
                  }
                  // Also remove from local tree immediately
                  const orphanKey = Object.keys(get().nodesById).find(k => get().nodesById[k].id === entityId);
                  if (orphanKey) get()._applyRemoveNode(orphanKey, { persist: false });
                } catch (cleanupErr) {
                  log.warn('[DataspaceTreeStore] Could not clean up orphaned edge:', entityId.slice(-8), cleanupErr);
                }
              }
              // Non-404 errors (auth, network) are silently ignored — the entity may just be
              // temporarily unavailable and should not be evicted from the tree.
            }
          }));
        }
      }

    } catch (error) {
      log.error('[DataspaceTreeStore] ❌ Failed to refresh edges:', error);
    }
  },
  
  // ========================================================================
  // Cleanup
  // ========================================================================
  
  /**
   * ✅ NEW: Migrate entities for a specific space (lazy migration)
   * Called when SpaceSidebarV2 detects no children for a space
   */
  migrateSpaceEntities: async (spaceId: string) => {
    const { dataspaceId } = get();
    if (!dataspaceId) {
      log.error('[DataspaceTreeStore] Cannot migrate - no dataspaceId');
      return;
    }
    
    log.log('[DataspaceTreeStore] 🔄 Migrating entities for space:', spaceId);
    
    // Get all entities in this space
    const entitiesInSpace = useEntityStore.getState().entities.filter(
      (e: BaseEntityType) => (e as any).spaceId === spaceId && isDisplayableEntity(e)
    );
    
    log.log('[DataspaceTreeStore] Found', entitiesInSpace.length, 'entities in space');
    
    if (entitiesInSpace.length === 0) return;
    
    // Check which ones already have edges (check ALL edges, not just space root!)
    // ✅ CRITICAL FIX: Check if entity has ANY edge, not just edges at space root
    // This prevents creating duplicate edges when entities are moved into folders
    const existingChildIds = new Set(
      Object.values(get().nodesById)
        .map((n: TreeNode) => n.id)  // ← Changed: removed parent filter!
    );
    
    const entitiesToMigrate = entitiesInSpace.filter((e: BaseEntityType) => !existingChildIds.has(e._id));
    
    if (entitiesToMigrate.length === 0) {
      log.log('[DataspaceTreeStore] All entities already have edges');
      return;
    }
    
    log.log('[DataspaceTreeStore] Migrating', entitiesToMigrate.length, 'entities');
    
    // Group by parent (from skeleton.treePosition or groupIds)
    const entitiesByParent = new Map<string | null, BaseEntityType[]>();
    
    entitiesToMigrate.forEach((entity: BaseEntityType) => {
      const treePos = (entity.skeleton as any)?.treePosition;
      let parentId: string | null;
      
      if (treePos && treePos.parentId) {
        parentId = treePos.parentId;
      } else {
        const groupIds = (entity.skeleton as any)?.groupIds || [];
        parentId = groupIds.length > 0 ? groupIds[0] : spaceId; // Default to space root
      }
      
      const group = entitiesByParent.get(parentId) || [];
      group.push(entity);
      entitiesByParent.set(parentId, group);
    });
    
    // Create edges with positions
    const edgesToCreate: any[] = [];
    
    entitiesByParent.forEach((entitiesInGroup, parentId) => {
      // Sort by existing treePosition or alphabetically
      entitiesInGroup.sort((a, b) => {
        const posA = (a.skeleton as any)?.treePosition?.pos || a.name;
        const posB = (b.skeleton as any)?.treePosition?.pos || b.name;
        return String(posA).localeCompare(String(posB));
      });
      
      entitiesInGroup.forEach((entity: BaseEntityType, index: number) => {
        const pos = generateCleanPosition(index);
        const treePos = (entity.skeleton as any)?.treePosition;
        
        edgesToCreate.push({
          parentId,
          childId: entity._id,
          pos,
          expanded: treePos?.expanded || false,
          kind: 'canonical'
        });
      });
    });
    
    // Batch create edges
    try {
      const createdEdges = await fetchService(`${API_BASE_URL}/dataspaces/${dataspaceId}/edges/batch`, {
        method: 'POST',
        body: JSON.stringify({ edges: edgesToCreate }),
        credentials: 'include',
      });
      
      log.log('[DataspaceTreeStore] ✅ Created', edgesToCreate.length, 'edges for space');
      
      // Add to local tree immediately
      if (Array.isArray(createdEdges)) {
        createdEdges.forEach((edge: any) => {
          const node: TreeNode = {
            id: edge.childId,
            parentId: edge.parentId,
            pos: edge.pos,
            expanded: edge.expanded,
            edgeId: edge.id,
            kind: edge.kind
          };
          const edgeKey = getEdgeKey(edge.childId, edge.parentId);
          get()._applyAddNode(edgeKey, node);
        });
        
        // ✅ CRITICAL: Rebuild children map to reflect new edges
        get()._rebuildChildrenMap();
        
        log.log('[DataspaceTreeStore] ✅ Space migration complete -', createdEdges.length, 'edges added to tree');
      }
      
    } catch (error) {
      log.error('[DataspaceTreeStore] ❌ Space migration failed:', error);
      console.error('❌ [DataspaceTreeStore] Migration error details:', error);
      
      // ✅ ROBUST FALLBACK: Even if migration fails, documents are still visible via skeleton
      // The skeleton-based fallback in SpaceSidebarV2 ensures documents show at root
      // This is intentional - better to show documents at wrong location than hide them
      console.warn('⚠️ [DataspaceTreeStore] Migration failed, but documents will still show via skeleton fallback');
      
      // ✅ FUTURE: Could add toast notification here to inform user
      // "Some documents may not be in their correct folders. Please refresh the page."
    }
  },
  
  cleanup: () => {
    const { entityStoreUnsubscribe, entityEventUnsubscribe, flushTimeoutId, refreshIntervalId, autoRefreshTimeoutId } = get();
    
    // Cleanup entity store subscription
    if (entityStoreUnsubscribe) {
      entityStoreUnsubscribe();
    }
    
    // ✅ NEW: Cleanup entity event subscription
    if (entityEventUnsubscribe) {
      entityEventUnsubscribe();
    }
    
    // Cleanup timers
    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId);
    }
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
    }
    if (autoRefreshTimeoutId) {
      clearTimeout(autoRefreshTimeoutId);
    }
    
    set({
      dataspaceId: null,
      nodesById: {},
      childrenByParentId: new Map(),
      pendingOps: [],
      initialized: false,
      rebuildCount: 0,
      entityStoreUnsubscribe: null,
      entityEventUnsubscribe: null,
      flushTimeoutId: null,
      isFlushInProgress: false,
      refreshIntervalId: null,
      autoRefreshTimeoutId: null
    });
  },
  
  // ========================================================================
  // Debug
  // ========================================================================
  
  debugPrintTree: () => {
    const { nodesById, getRootNodeIds, getChildNodeIds } = get();
    const entityStore = useEntityStore.getState();
    
    log.log('\n🌲 DATASPACE TREE:');
    
    const printNode = (nodeId: string, depth: number = 0) => {
      const node = nodesById[nodeId];
      const entity = entityStore.entities.find((e: BaseEntityType) => e._id === nodeId);
      const indent = '  '.repeat(depth);
      const icon = node?.expanded ? '📂' : '📁';
      
      log.log(`${indent}${icon} ${entity?.name || 'Unknown'} (${node?.pos || 'no-pos'}) [${nodeId.slice(-4)}]`);
      
      if (node?.expanded) {
        const children = getChildNodeIds(nodeId);
        children.forEach(childId => printNode(childId, depth + 1));
      }
    };
    
    const roots = getRootNodeIds();
    log.log(`Root level (${roots.length} items):`);
    roots.forEach((id: string) => printNode(id, 0));
    log.log('\n');
  }
}));

export default useDataspaceTreeStore;

// Flush any pending tree operations when the user closes or navigates away.
// Uses the same pattern as spaceTreeStore — the fetch may not complete before
// the page unloads, but it at least initiates the request, which modern browsers
// keep alive briefly after beforeunload for in-flight XHR/fetch.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    const state = useDataspaceTreeStore.getState();
    if (state.pendingOps.length > 0) {
      log.log('[DataspaceTreeStore] ⚠️ Page unloading with pending ops, flushing immediately...');
      state._flushToBackend();
    }
  });
}
